import If from 'components/If';
import ProcessingButton from 'components/ProcessingButton';
import SimpleModal from 'components/SimpleModal';
import {
  FormDateTime,
  FormInput,
  FormObject,
  FormSelect,
  FormState,
  Labeled,
  FloatType,
} from 'component_utils/FormUtils';
import { useEffectAsync } from 'component_utils/utils';
import { IProductionResource, IWorkOrder, IWorkOrderStep } from 'interfaces';
import { IProductionWorkTime } from 'interfaces/entities-production';
import React, { FC, useMemo, useState } from 'react';
import { useApi } from 'utils/API';
import { IWorkOrderStepMap } from 'utils/endpoints/Production';
import { useTranslations } from 'utils/language';
import Translation from 'utils/Language/Translation';
import { addNotification } from 'utils/NotificationManager';
import { sortBy } from 'lodash';

const emptyList: any[] = [];
const Editor: FC<{
  getTemplate: () => IProductionWorkTime;
  workOrders: IWorkOrder[];
  workOrderSteps: IWorkOrderStepMap;
  workCenters: IProductionResource[];
  reload: any;
  trigger: (open: any) => any;
}> = ({ trigger, getTemplate, workOrders, workOrderSteps, workCenters, reload }) => {
  const [state, setState] = useState<IProductionWorkTime>({} as IProductionWorkTime);
  const T = useTranslations()
  const api = useApi();

  const steps = useMemo(() => sortBy(workOrderSteps[state.workOrderCode] || (emptyList as IWorkOrderStep[]), it => it.stepNumber), [workOrderSteps, state.workOrderCode])
  useEffectAsync(async () => {
    if (state.workOrderStep && !state.workCenter) {
      const step = steps.find((it) => it.stepCode === state.workOrderStep);
      if (step) {
        setState((old) => ({
          ...old,
          workCenter: step?.plannedWorkCenterCode || '',
        }));
      }
    }
  }, [setState, steps, state.workOrderStep]);

  return (
    <SimpleModal
      fullscreen
      title={<Translation name="T.misc.editWorkedTimeslots" />}
      trigger={trigger}
      onOpen={() => {
        setState(getTemplate());
      }}
    >
      {(close) => (
        <FormState state={state} setState={setState} containerProps={{ className: 'flex-container' }}>
          <div className={'nested-flex-container'}>
            <Labeled title={<Translation name="T.objects.workConfirmation.workOrderCode" />}>
              <FormObject<IWorkOrder>
                path="workOrderCode"
                autocomplete={(s) =>
                  workOrders
                    .filter((it) => it.visualCode.includes(s) || it.producedItem.visualCode.includes(s))
                    .map((it) => ({
                      label: `${it.visualCode} - ${it.producedItem.visualCode}`,
                      value: it.code,
                    }))
                    .slice(0, 10)
                }
                find={(s) => workOrders.find((it) => it.code === s || it.visualCode === s)}
                getCode={(it) => it.code}
                render={(it) => (
                  <>
                    {it.visualCode} - {it.producedItem.visualCode}
                  </>
                )}
              />
            </Labeled>

            <Labeled title={<Translation name="T.objects.workConfirmation.workOrderStep" />}>
              <FormSelect
                path="workOrderStep"
                options={[
                  {
                    label: '',
                    value: '',
                  },
                  ...steps.map((it) => ({
                    label: `${it.stepCode} - ${it.description}`,
                    value: it.stepCode,
                  })),
                ]}
              />
            </Labeled>

            <Labeled title={<Translation name="T.objects.workConfirmation.workCenter" />}>
              <FormObject<IProductionResource>
                path="workCenter"
                autocomplete={(s) =>
                  workCenters
                    .filter((it) => it.visualCode.includes(s) || it.name.includes(s))
                    .map((it) => ({
                      label: `${it.visualCode} - ${it.name}`,
                      value: it.code,
                    }))
                }
                find={(s) => workCenters.find((it) => it.code === s || it.visualCode === s)}
                getCode={(it) => it.code}
                render={(it) => (
                  <>
                    {it.visualCode} - {it.name}
                  </>
                )}
              />
            </Labeled>

            <Labeled title={<Translation name="T.objects.workConfirmation.workType" />}>
              <FormSelect
                path='workTimeType'
                options={[
                  { label: "", value: "" },
                  { label: T("T.objects.workConfirmation.workTypeSetup"), value: "SETUP" },
                  { label: T("T.objects.workConfirmation.workTypeWork"), value: "WORK" },
                ]}
              />
            </Labeled>

            <If cond={state.workTimeType === 'WORK'}>
              <Labeled title={<Translation name="T.objects.workConfirmation.produced" />}>
                <FormInput path="produced" typed={FloatType} />
              </Labeled>

              <Labeled title={<Translation name="T.objects.workConfirmation.scrap" />}>
                <FormInput path="scrap" typed={FloatType} />
              </Labeled>
            </If>

            <Labeled title={<Translation name="T.objects.workConfirmation.startTime" />}>
              <FormDateTime path="startTime" />
            </Labeled>

            <Labeled title={<Translation name="T.objects.workConfirmation.endTime" />}>
              <FormDateTime path="endTime" />
            </Labeled>
          </div>

          <ProcessingButton
            block
            color={state.endTime ? 'primary' : 'success'}
            onClick={async () => {
              // make sure the configuration is valid
              if (
                !workOrders.find((it) => it.code === state.workOrderCode) ||
                !workOrderSteps[state.workOrderCode].find((it) => it.stepCode === state.workOrderStep) ||
                !(!state.workCenter || workCenters.find((it) => it.code === state.workCenter))
              ) {
                throw addNotification('danger', <Translation name="T.errors.invalidInput" />);
              }

              await api.production.saveWorkTimes({
                ...state,
                endTime: state.endTime ? state.endTime : null,
              });
              reload();
              close();
            }}
          >
            <Translation name={state.endTime ? 'T.misc.save' : 'T.misc.start'} />
          </ProcessingButton>
        </FormState>
      )}
    </SimpleModal>
  );
};
export default Editor;
